import { Link, useNavigate, useLocation } from "react-router-dom";
import { useRole, ROLES } from "../context/RoleContext";
import { Button } from "./ui/button";
import { LogOut, Menu, Plane } from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/utils";
import { ThemeToggle } from "./ThemeToggle";

export function AppHeader() {
  const { role, setRole, setTravelerName, setActiveTripId } = useRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    setRole(null);
    setTravelerName("");
    setActiveTripId(null);
    localStorage.clear();
    navigate("/");
  };

  const adminNav = [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/records", label: "Records" },
    { to: "/admin/reports", label: "Reports" },
  ];

  const travelerNav = [
    { to: "/traveler/submit", label: "Submit Trip" },
    { to: "/traveler/status", label: "My Trip" },
  ];

  const nav = role === ROLES.ADMIN ? adminNav : role === ROLES.TRAVELER ? travelerNav : [];

  return (
    <header className="glass-nav sticky top-0 z-50" data-testid="app-header">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={role ? (role === ROLES.ADMIN ? "/admin" : "/traveler/submit") : "/"} className="flex items-center gap-3 group" data-testid="header-logo">
          <img
            src="/carrey-logo.png"
            alt="Carrey Transportation"
            className="h-12 w-auto drop-shadow-[0_0_18px_rgba(240,125,0,0.25)] group-hover:drop-shadow-[0_0_22px_rgba(240,125,0,0.4)] transition-all"
          />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-heading text-base font-semibold tracking-tight text-white">CARREY</span>
            <span className="text-[10px] tracking-[0.25em] text-zinc-400 uppercase">Transportation Hub</span>
          </div>
        </Link>

        {role && (
          <>
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((item) => {
                const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={cn(
                      "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                      active
                        ? "bg-white/10 text-white"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              <ThemeToggle />
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
                <div className={cn(
                  "h-2 w-2 rounded-full",
                  role === ROLES.ADMIN ? "bg-[#F07D00]" : "bg-cyan-400"
                )} />
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-300">
                  {role === ROLES.ADMIN ? "Admin" : "Traveler"}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-zinc-400 hover:text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" /> Switch Role
              </Button>
            </div>

            <div className="md:hidden flex items-center gap-2">
              <ThemeToggle size="sm" />
              <button
                onClick={() => setOpen(!open)}
                className="p-2 rounded-lg bg-white/5 text-white"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
      </div>

      {open && role && (
        <div className="md:hidden border-t border-white/10 bg-[#0B0C10]/95 backdrop-blur-xl">
          <div className="px-4 py-3 space-y-1">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-300 hover:bg-white/5"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
            ))}
            <button
              onClick={() => { setOpen(false); handleLogout(); }}
              className="w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:bg-white/5"
              data-testid="mobile-logout-button"
            >
              Switch Role
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
